import { useCallback, useMemo, useReducer } from 'react';
import { redactBase64FromAiPrompt } from '../NewBlogSystem/utilities/htmlAiRequest.js';

export const AI_QUEUE_STATUS = Object.freeze({
  PENDING: 'pending',
  AWAITING_CONFIRMATION: 'awaiting-confirmation',
  RUNNING: 'running',
  AWAITING_REVIEW: 'awaiting-review',
  FAILED: 'failed',
});

const EDITABLE_STATUSES = new Set([
  AI_QUEUE_STATUS.PENDING,
  AI_QUEUE_STATUS.AWAITING_CONFIRMATION,
  AI_QUEUE_STATUS.FAILED,
]);

const TASK_KEYS = new Set([
  'type', 'key', 'label', 'contextLabel', 'nodeId', 'targetId', 'position',
  'previewId', 'scoreMessageId', 'recommendationIndex', 'recordId', 'kind',
]);

let requestSequence = 0;
const nextRequestId = () => `ai-request-${Date.now()}-${++requestSequence}`;

const safeText = (value, maxLength = 8000) => String(
  redactBase64FromAiPrompt(String(value || '')) || ''
).trim().slice(0, maxLength);

export const sanitizeQueuedTask = (task = {}) => Object.fromEntries(
  Object.entries(task)
    .filter(([key, value]) => TASK_KEYS.has(key) && (
      typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ))
    .map(([key, value]) => [key, typeof value === 'string' ? safeText(value, 500) : value])
);

export const createQueuedAiRequest = (task, prompt = '') => ({
  id: nextRequestId(),
  task: sanitizeQueuedTask(task),
  prompt: safeText(prompt),
  status: AI_QUEUE_STATUS.PENDING,
  error: '',
  confirmed: false,
  createdAt: new Date().toISOString(),
});

export const initialAiRequestQueueState = {
  requests: [],
  activeId: null,
};

const replaceRequest = (requests, requestId, update) => requests.map((request) => (
  request.id === requestId ? { ...request, ...update } : request
));

const editableRequest = (state, requestId) => state.requests.find((request) => (
  request.id === requestId && EDITABLE_STATUSES.has(request.status)
));

export const aiRequestQueueReducer = (state, action) => {
  switch (action.type) {
    case 'enqueue':
      return { ...state, requests: [...state.requests, action.request] };
    case 'start': {
      if (state.activeId || !state.requests.some((request) => (
        request.id === action.id && request.status === AI_QUEUE_STATUS.PENDING
      ))) return state;
      return {
        ...state,
        activeId: action.id,
        requests: replaceRequest(state.requests, action.id, {
          status: AI_QUEUE_STATUS.RUNNING,
          error: '',
        }),
      };
    }
    case 'set-status': {
      if (state.activeId !== action.id) return state;
      return {
        ...state,
        requests: replaceRequest(state.requests, action.id, {
          status: action.status,
          ...(action.error !== undefined ? { error: safeText(action.error, 1200) } : {}),
          ...(action.confirmed !== undefined ? { confirmed: Boolean(action.confirmed) } : {}),
        }),
      };
    }
    case 'complete':
      return state.activeId === action.id
        ? {
          requests: state.requests.filter((request) => request.id !== action.id),
          activeId: null,
        }
        : state;
    case 'update': {
      const current = editableRequest(state, action.id);
      if (!current) return state;
      const nextTask = action.task ? sanitizeQueuedTask(action.task) : current.task;
      const nextPrompt = action.prompt !== undefined ? safeText(action.prompt) : current.prompt;
      return {
        activeId: state.activeId === action.id ? null : state.activeId,
        requests: replaceRequest(state.requests, action.id, {
          task: nextTask,
          prompt: nextPrompt,
          status: AI_QUEUE_STATUS.PENDING,
          error: '',
          confirmed: false,
        }),
      };
    }
    case 'remove': {
      if (!editableRequest(state, action.id)) return state;
      return {
        requests: state.requests.filter((request) => request.id !== action.id),
        activeId: state.activeId === action.id ? null : state.activeId,
      };
    }
    case 'retry': {
      const current = state.requests.find((request) => (
        request.id === action.id && request.status === AI_QUEUE_STATUS.FAILED
      ));
      if (!current) return state;
      return {
        activeId: null,
        requests: replaceRequest(state.requests, action.id, {
          status: AI_QUEUE_STATUS.PENDING,
          error: '',
          confirmed: false,
        }),
      };
    }
    case 'move': {
      const fromIndex = state.requests.findIndex((request) => request.id === action.id);
      const toIndex = state.requests.findIndex((request) => request.id === action.targetId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return state;
      if (!EDITABLE_STATUSES.has(state.requests[fromIndex].status)
        || !EDITABLE_STATUSES.has(state.requests[toIndex].status)) return state;
      const requests = [...state.requests];
      const [moved] = requests.splice(fromIndex, 1);
      requests.splice(toIndex, 0, {
        ...moved,
        status: AI_QUEUE_STATUS.PENDING,
        confirmed: false,
      });
      return {
        requests,
        activeId: state.activeId === action.id ? null : state.activeId,
      };
    }
    case 'move-offset': {
      const fromIndex = state.requests.findIndex((request) => request.id === action.id);
      const toIndex = fromIndex + action.offset;
      if (fromIndex < 0 || toIndex < 0 || toIndex >= state.requests.length) return state;
      return aiRequestQueueReducer(state, {
        type: 'move', id: action.id, targetId: state.requests[toIndex].id,
      });
    }
    case 'reset':
      return initialAiRequestQueueState;
    default:
      return state;
  }
};

export const useAiRequestQueue = () => {
  const [state, dispatch] = useReducer(aiRequestQueueReducer, initialAiRequestQueueState);
  const enqueue = useCallback((task, prompt = '') => {
    const request = createQueuedAiRequest(task, prompt);
    dispatch({ type: 'enqueue', request });
    return request;
  }, []);
  const start = useCallback((id) => dispatch({ type: 'start', id }), []);
  const setStatus = useCallback((id, status, options = {}) => (
    dispatch({ type: 'set-status', id, status, ...options })
  ), []);
  const complete = useCallback((id) => dispatch({ type: 'complete', id }), []);
  const update = useCallback((id, values) => dispatch({ type: 'update', id, ...values }), []);
  const remove = useCallback((id) => dispatch({ type: 'remove', id }), []);
  const retry = useCallback((id) => dispatch({ type: 'retry', id }), []);
  const move = useCallback((id, targetId) => dispatch({ type: 'move', id, targetId }), []);
  const moveBy = useCallback((id, offset) => dispatch({ type: 'move-offset', id, offset }), []);
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);
  const activeRequest = useMemo(() => (
    state.requests.find((request) => request.id === state.activeId) || null
  ), [state.activeId, state.requests]);
  const nextRequest = useMemo(() => (
    state.requests.find((request) => request.status === AI_QUEUE_STATUS.PENDING) || null
  ), [state.requests]);

  return {
    ...state,
    activeRequest,
    nextRequest,
    pendingCount: state.requests.filter((request) => request.status === AI_QUEUE_STATUS.PENDING).length,
    needsAttention: Boolean(activeRequest && [
      AI_QUEUE_STATUS.AWAITING_CONFIRMATION,
      AI_QUEUE_STATUS.AWAITING_REVIEW,
      AI_QUEUE_STATUS.FAILED,
    ].includes(activeRequest.status)),
    enqueue,
    start,
    setStatus,
    complete,
    update,
    remove,
    retry,
    move,
    moveBy,
    reset,
  };
};

